import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, History, Megaphone, RefreshCw, Clock, Zap, Search, Users, X } from 'lucide-react';
import { useAdminPushNotifications, TargetType, PushNotificationPayload, ScheduledNotificationType } from '@/hooks/useAdminPushNotifications';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const TIERS = ['Seed', 'Spark', 'Flame', 'Ember', 'Blaze', 'Inferno', 'Legend', 'Titan', 'Immortal', 'Ascended', 'Mythic', 'Eternal'];
const GOALS = ['muscle', 'strength', 'weight_loss', 'athletic', 'general'];
const EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];

const SCHEDULED_NOTIFICATION_TYPES: { value: ScheduledNotificationType; label: string; icon: string; description: string }[] = [
  { value: 'weigh_in', label: 'Weigh-In Reminder', icon: '⚖️', description: 'Morning weigh-in nudge' },
  { value: 'motivation', label: 'Daily Motivation', icon: '🔥', description: 'Motivational quote' },
  { value: 'training_reminder', label: 'Training Reminder', icon: '💪', description: 'Evening training nudge' },
];

export function AdminPushPanel() {
  const { 
    sendNotification, 
    sending, 
    logs, 
    logsLoading, 
    fetchLogs,
    triggerScheduledNotification,
    testingSending 
  } = useAdminPushNotifications();
  
  const { users, loading: usersLoading, fetchUsers } = useAdminUsers();
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [url, setUrl] = useState('');
  const [targetType, setTargetType] = useState<TargetType>('all');
  const [targetValue, setTargetValue] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Specific users state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');

  // Load logs on mount
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  // Fetch users when "specific" target type is selected
  useEffect(() => {
    if (targetType === 'specific') {
      fetchUsers(1, userSearch);
    }
  }, [targetType, userSearch, fetchUsers]);
  
  // Clear selected users when switching away from "specific"
  useEffect(() => {
    if (targetType !== 'specific') {
      setSelectedUserIds([]);
      setUserSearch('');
    }
  }, [targetType]);
  
  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users;
    const search = userSearch.toLowerCase();
    return users.filter(user => 
      user.name?.toLowerCase().includes(search) ||
      user.display_name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  }, [users, userSearch]);
  
  // Get selected user objects for display
  const selectedUsers = useMemo(() => {
    return users.filter(user => selectedUserIds.includes(user.id));
  }, [users, selectedUserIds]);
  
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  const removeSelectedUser = (userId: string) => {
    setSelectedUserIds(prev => prev.filter(id => id !== userId));
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    
    if (targetType === 'specific' && selectedUserIds.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    const payload: PushNotificationPayload = {
      title: title.trim(),
      message: message.trim(),
      url: url.trim() || undefined,
      targetType,
      targetValue: targetValue || undefined,
      userIds: targetType === 'specific' ? selectedUserIds : undefined,
    };

    const result = await sendNotification(payload);

    if (result.success) {
      toast.success('Push notification sent successfully!');
      // Reset form
      setTitle('');
      setMessage('');
      setUrl('');
      setTargetType('all');
      setTargetValue('');
      setSelectedUserIds([]);
      setUserSearch('');
      setShowConfirm(false);
      // Refresh logs
      fetchLogs();
    } else {
      toast.error(result.error || 'Failed to send notification');
    }
  };

  const handleTestScheduledNotification = async (type: ScheduledNotificationType) => {
    const result = await triggerScheduledNotification(type);
    
    if (result.success) {
      const data = result.data as { sent?: number; message?: string };
      if (data?.sent && data.sent > 0) {
        toast.success(`Sent ${data.sent} ${type.replace('_', ' ')} notifications!`);
      } else {
        toast.info(data?.message || 'No eligible users found (push_enabled may be false)');
      }
      fetchLogs();
    } else {
      toast.error(result.error || 'Failed to trigger notification');
    }
  };

  const getTargetLabel = (type: string, filter: Record<string, unknown> | null): string => {
    if (type === 'all') return 'All Users';
    if (type === 'specific') return 'Specific Users';
    if (type === 'scheduled_batch') return 'Scheduled';
    if (filter?.value) return `${type}: ${filter.value}`;
    return type;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">{logs.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-primary">Broadcasts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">
              {logs.filter(l => l.target_type === 'all').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-400">Segmented</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">
              {logs.filter(l => ['tier', 'goal', 'experience'].includes(l.target_type)).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-400">Scheduled</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">
              {logs.filter(l => l.target_type === 'scheduled_batch').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Test Scheduled Notifications Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Test Scheduled Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Manually trigger scheduled notifications to test delivery. Only sends to users with push_enabled = true.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SCHEDULED_NOTIFICATION_TYPES.map((notifType) => (
              <Button
                key={notifType.value}
                variant="outline"
                onClick={() => handleTestScheduledNotification(notifType.value)}
                disabled={testingSending}
                className="flex flex-col items-start h-auto py-3 px-4"
              >
                <div className="flex items-center gap-2 w-full">
                  <span className="text-lg">{notifType.icon}</span>
                  <span className="font-medium">{notifType.label}</span>
                  {testingSending && <Zap className="h-4 w-4 ml-auto animate-pulse" />}
                </div>
                <span className="text-xs text-muted-foreground mt-1">{notifType.description}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Compose Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Compose Push Notification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="push-title">Title *</Label>
            <Input
              id="push-title"
              placeholder="e.g., 🔥 New Feature Alert!"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">{title.length}/50 characters</p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="push-message">Message *</Label>
            <Textarea
              id="push-message"
              placeholder="e.g., Check out the new workout tracking features!"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={250}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">{message.length}/250 characters</p>
          </div>

          {/* URL (optional) */}
          <div className="space-y-2">
            <Label htmlFor="push-url">URL (optional)</Label>
            <Input
              id="push-url"
              placeholder="e.g., https://app.evolve.fit/new-feature"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Opens when user taps the notification</p>
          </div>

          {/* Target Selection */}
          <div className="space-y-3">
            <Label>Target Audience</Label>
            <RadioGroup
              value={targetType}
              onValueChange={(v) => {
                setTargetType(v as TargetType);
                setTargetValue('');
              }}
              className="grid grid-cols-2 md:grid-cols-5 gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="target-all" />
                <Label htmlFor="target-all" className="flex items-center gap-1 cursor-pointer">
                  <Megaphone className="h-4 w-4" />
                  All Users
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="tier" id="target-tier" />
                <Label htmlFor="target-tier" className="cursor-pointer">By Tier</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="goal" id="target-goal" />
                <Label htmlFor="target-goal" className="cursor-pointer">By Goal</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="experience" id="target-exp" />
                <Label htmlFor="target-exp" className="cursor-pointer">By Experience</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="target-specific" />
                <Label htmlFor="target-specific" className="flex items-center gap-1 cursor-pointer">
                  <Users className="h-4 w-4" />
                  Specific Users
                </Label>
              </div>
            </RadioGroup>

            {/* Conditional Dropdowns */}
            {targetType === 'tier' && (
              <Select value={targetValue} onValueChange={setTargetValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tier..." />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map(tier => (
                    <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {targetType === 'goal' && (
              <Select value={targetValue} onValueChange={setTargetValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select goal..." />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map(goal => (
                    <SelectItem key={goal} value={goal}>
                      {goal.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {targetType === 'experience' && (
              <Select value={targetValue} onValueChange={setTargetValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select experience level..." />
                </SelectTrigger>
                <SelectContent>
                  {EXPERIENCE_LEVELS.map(exp => (
                    <SelectItem key={exp} value={exp}>
                      {exp.charAt(0).toUpperCase() + exp.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Specific Users Picker */}
            {targetType === 'specific' && (
              <div className="space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                {/* Selected Users Badges */}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-muted-foreground">Selected ({selectedUsers.length}):</span>
                    {selectedUsers.map(user => (
                      <Badge 
                        key={user.id} 
                        variant="secondary"
                        className="flex items-center gap-1 pr-1"
                      >
                        {user.name || user.display_name || user.email || 'Unknown'}
                        <button
                          type="button"
                          onClick={() => removeSelectedUser(user.id)}
                          className="ml-1 hover:bg-muted rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* User List */}
                <ScrollArea className="h-48 border border-border rounded-lg bg-background">
                  {usersLoading ? (
                    <div className="p-4 space-y-2">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      {userSearch ? 'No users found matching your search' : 'No users available'}
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded-md cursor-pointer"
                          onClick={() => toggleUserSelection(user.id)}
                        >
                          <Checkbox
                            checked={selectedUserIds.includes(user.id)}
                            onCheckedChange={() => toggleUserSelection(user.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {user.name || user.display_name || 'No name'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {user.email || 'No email'}
                            </p>
                          </div>
                          {user.is_premium && (
                            <Badge variant="default" className="text-xs">Premium</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Preview */}
          {(title || message) && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground mb-2">Preview</p>
              <div className="space-y-1">
                <p className="font-semibold text-sm">{title || 'Notification Title'}</p>
                <p className="text-sm text-muted-foreground">{message || 'Notification message...'}</p>
              </div>
            </div>
          )}

          {/* Send Button */}
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={sending || !title.trim() || !message.trim() || (targetType === 'specific' && selectedUserIds.length === 0)}
            className="w-full"
          >
            {sending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Push Notification
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Push Notification?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send a push notification to {targetType === 'all' ? 'all users' : 
                targetType === 'specific' ? `${selectedUserIds.length} selected user${selectedUserIds.length !== 1 ? 's' : ''}` : 
                `users with ${targetType}: ${targetValue}`}.
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="font-semibold text-foreground">{title}</p>
                <p className="text-sm text-muted-foreground mt-1">{message}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSend}>
              <Send className="h-4 w-4 mr-2" />
              Send Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recent Notifications History */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Recent Notifications
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => fetchLogs()} disabled={logsLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${logsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {logsLoading && logs.length === 0 ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No notifications sent yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.slice(0, 10).map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{log.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{log.message}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getTargetLabel(log.target_type, log.target_filter)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={log.notification_type === 'admin' ? 'default' : 'secondary'}>
                        {log.notification_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
